import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api, type Finding, type ScanFlowRibbon } from "@/api/client";
import { DetailPanel, KeyValue, PanelSection } from "@/components/DetailPanel";
import { PageHeader } from "@/components/PageHeader";
import { SankeyDiagram } from "@/components/SankeyDiagram";

const SEVERITY: Record<
  Finding["severity"],
  { rule: string; pill: string; glow: string; icon: string; accent: "crit" | "warn" | "cyber" }
> = {
  critical: {
    rule: "rule-crit",
    pill: "bg-crit/15 text-crit-400 border-crit/40",
    glow: "text-glow-crit text-crit-400",
    icon: "✕",
    accent: "crit",
  },
  high: {
    rule: "rule-crit",
    pill: "bg-crit/15 text-crit-400 border-crit/40",
    glow: "text-glow-crit text-crit-400",
    icon: "▲",
    accent: "crit",
  },
  medium: {
    rule: "rule-warn",
    pill: "bg-warn/15 text-warn-400 border-warn/40",
    glow: "text-glow-warn text-warn-400",
    icon: "◆",
    accent: "warn",
  },
  low: {
    rule: "rule-info",
    pill: "bg-cyber/15 text-cyber-400 border-cyber/40",
    glow: "text-glow-cyber text-cyber-400",
    icon: "•",
    accent: "cyber",
  },
};

const STATUS: Record<Finding["status"], string> = {
  open: "bg-crit/15 text-crit-400",
  acknowledged: "bg-chrome-400/10 text-chrome-300",
  resolved: "bg-ok/15 text-ok-400",
};

export function FindingsPage(): JSX.Element {
  const findingsQ = useQuery({ queryKey: ["findings"], queryFn: api.findings });
  const flowQ = useQuery({ queryKey: ["scans", "flow"], queryFn: api.scanFlow });
  const devicesQ = useQuery({ queryKey: ["devices"], queryFn: api.devices });
  const [activeId, setActiveId] = useState<string | null>(null);

  const findings = useMemo(
    () => findingsQ.data?.findings ?? [],
    [findingsQ.data],
  );
  const devices = devicesQ.data?.devices ?? [];
  const flow = flowQ.data;

  const active = useMemo(
    () => findings.find((f) => f.id === activeId) ?? null,
    [findings, activeId],
  );

  const onSelectRibbon = (r: ScanFlowRibbon) => {
    if (r.finding_id) setActiveId(r.finding_id);
  };

  if (findingsQ.isLoading) return <main className="text-chrome-300">Loading…</main>;
  if (findingsQ.error)
    return <main className="text-crit">Failed to load findings.</main>;

  if (findings.length === 0) {
    return (
      <main>
        <PageHeader
          eyebrow="discovered exposures"
          title="Findings"
          subtitle="No findings yet. Run a scan once your shim has discovered devices on the LAN."
        />
        <div className="panel p-10 text-center text-sm text-chrome-400">
          The Findings page populates as the shim runs nuclei templates against
          your devices and reports matches back. Visit the Devices page to see
          what the shim has discovered so far.
        </div>
      </main>
    );
  }

  const counts = findings.reduce<Record<string, number>>(
    (acc, f) => ({ ...acc, [f.severity]: (acc[f.severity] ?? 0) + 1 }),
    {},
  );
  const deviceById = new Map(devices.map((d) => [d.id, d]));

  return (
    <main>
      <PageHeader
        eyebrow="discovered exposures"
        title="Findings"
        subtitle="What we tried, where it landed, and what you can do about it."
        right={
          <div className="flex items-center gap-2">
            {(["high", "medium", "low"] as const).map((sev) => (
              <div
                key={sev}
                className={[
                  "flex items-center gap-2 rounded border px-3 py-1.5",
                  SEVERITY[sev].pill,
                ].join(" ")}
              >
                <span className="text-[10px] font-medium uppercase tracking-widest">
                  {sev}
                </span>
                <span className={["text-sm font-semibold tabular-nums", SEVERITY[sev].glow].join(" ")}>
                  {counts[sev] ?? 0}
                </span>
              </div>
            ))}
          </div>
        }
      />

      {flow && devices.length > 0 && (
        <section className="panel mb-8 p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="label-overline">scan flow · scenarios → devices</div>
              <div className="mt-1 text-sm text-chrome-300">
                Ribbon thickness reflects attempts; color reflects outcome.
              </div>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              {[
                { c: "#84CC16", l: "caught" },
                { c: "#22D3EE", l: "low finding" },
                { c: "#F59E0B", l: "medium finding" },
                { c: "#F43F5E", l: "high finding" },
              ].map((entry) => (
                <span key={entry.l} className="flex items-center gap-1.5 text-chrome-300">
                  <span
                    className="size-2 rounded-sm"
                    style={{ backgroundColor: entry.c }}
                  />
                  {entry.l}
                </span>
              ))}
            </div>
          </div>
          <SankeyDiagram
            flow={flow}
            devices={devices}
            onSelectRibbon={onSelectRibbon}
          />
          <div className="mt-3 text-center text-xs text-chrome-400">
            click a ribbon to drill into the finding
          </div>
        </section>
      )}

      <ul className="space-y-3">
        {findings.map((f, i) => {
          const sev = SEVERITY[f.severity];
          return (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => setActiveId(f.id)}
                className={[
                  "panel relative block w-full overflow-hidden py-5 pl-6 pr-5 text-left transition",
                  "hover:border-cyber-400/40",
                  sev.rule,
                ].join(" ")}
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={["text-base font-bold", sev.glow].join(" ")}
                    aria-hidden="true"
                  >
                    {sev.icon}
                  </span>
                  <span
                    className={[
                      "rounded border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest",
                      sev.pill,
                    ].join(" ")}
                  >
                    {f.severity}
                  </span>
                  <span className="text-[10px] font-medium uppercase tracking-widest text-chrome-400">
                    {f.kill_chain_stage.replace(/_/g, " ")}
                  </span>
                  <span
                    className={[
                      "ml-auto rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest",
                      STATUS[f.status],
                    ].join(" ")}
                  >
                    {f.status}
                  </span>
                </div>
                <h2 className="mt-3 text-base font-semibold text-chrome-50">{f.title}</h2>
                <p className="mt-1 max-w-3xl text-sm leading-relaxed text-chrome-300">
                  {f.plain_summary}
                </p>
                <div className="mt-4 flex items-center gap-4 text-[11px] text-chrome-400">
                  <span>
                    detected {new Date(f.detected_at).toLocaleString([], { hour12: false })}
                  </span>
                  <span className="text-chrome-500">·</span>
                  <span>
                    {deviceById.get(f.device_id)?.display_name ?? "unknown device"}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      <DetailPanel
        open={!!active}
        onClose={() => setActiveId(null)}
        eyebrow="finding · technical detail"
        title={active?.title ?? ""}
        accent={active ? SEVERITY[active.severity].accent : "cyber"}
      >
        {active && (
          <>
            <PanelSection title="overview">
              <p className="text-sm leading-relaxed text-chrome-200">
                {active.plain_summary}
              </p>
            </PanelSection>
            <PanelSection title="metadata">
              <KeyValue label="severity">
                <span className={SEVERITY[active.severity].glow}>
                  {active.severity}
                </span>
              </KeyValue>
              <KeyValue label="status">{active.status}</KeyValue>
              <KeyValue label="kill-chain stage">
                {active.kill_chain_stage.replace(/_/g, " ")}
              </KeyValue>
              <KeyValue label="detected">
                {new Date(active.detected_at).toLocaleString([], { hour12: false })}
              </KeyValue>
            </PanelSection>
            <PanelSection title="affected device">
              <KeyValue label="name">
                {deviceById.get(active.device_id)?.display_name ?? "unknown"}
              </KeyValue>
              <KeyValue label="device id" mono>
                {active.device_id}
              </KeyValue>
              {deviceById.get(active.device_id)?.ip && (
                <KeyValue label="ip" mono>
                  {deviceById.get(active.device_id)?.ip}
                </KeyValue>
              )}
              {deviceById.get(active.device_id)?.mac && (
                <KeyValue label="mac" mono>
                  {deviceById.get(active.device_id)?.mac}
                </KeyValue>
              )}
            </PanelSection>
            <PanelSection title="identifiers">
              <KeyValue label="finding id" mono>
                {active.id}
              </KeyValue>
            </PanelSection>
          </>
        )}
      </DetailPanel>
    </main>
  );
}
